import { Injectable } from '@angular/core';
import { ErrorPolicyService, muteError } from '@mri-platform/angular-error-handling';
import { UserScopedCacheService } from '@mri-platform/dsg/core';
import { Report, reportInfoQueries, ReportInfoRepositoryService } from '@mri-platform/dsg/entity-state';
import { ClaimsAuthzService, CrudAllAction } from '@mri-platform/resource-authz';
import { AuthService } from '@mri/angular-wfe-proxy-oidc';
import { concat, defer, map, Observable, of, shareReplay, switchMap, take, tap } from 'rxjs';
import { DASHBOARDS_AVAILABILITY_KEY, THREE_MONTHS } from '../models';

const viewAllPermission = Report.authorization[CrudAllAction.Read];

@Injectable({ providedIn: 'root' })
export class DashboardsAvailabilityService {
  private hasDashboardsResult: boolean | undefined;

  constructor(
    private readonly authService: AuthService,
    private readonly claimsAuthzService: ClaimsAuthzService,
    private readonly repo: ReportInfoRepositoryService,
    private readonly errorPolicy: ErrorPolicyService,
    private readonly cacheService: UserScopedCacheService
  ) {}

  readonly hasDashboards$: Observable<boolean> = this.authService.userLoaded$.pipe(
    switchMap(({ permissions }) => {
      const hasViewAll = this.claimsAuthzService.checkAccess(viewAllPermission, permissions).isGranted;
      if (hasViewAll) return of(true);
      if (this.hasDashboardsResult != null) return of(this.hasDashboardsResult);
      return defer(() => this.repo.executeQuery(reportInfoQueries.selectHasAny)).pipe(
        map(results => results.length > 0),
        tap(result => (this.hasDashboardsResult = result))
      );
    }),
    tap(hasDashboards => this.cacheService.setItem(DASHBOARDS_AVAILABILITY_KEY, hasDashboards, { ttl: THREE_MONTHS })),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  readonly hasDashboardsCachedSafe$: Observable<boolean> = concat(
    this.getFromCache<boolean>(DASHBOARDS_AVAILABILITY_KEY).pipe(
      map(value => value ?? false),
      take(1)
    ),
    this.hasDashboards$
  ).pipe(this.errorPolicy.catchHandleReplace(false, muteError), shareReplay({ refCount: true, bufferSize: 1 }));

  private getFromCache<T>(key: string): Observable<T | null> {
    return defer(() => this.cacheService.getItemAsync<T>(key)).pipe(
      this.errorPolicy.catchHandleReplace(null, muteError)
    );
  }
}
